// Notities op projectniveau (SPEC 27okt 6): vrije notities met auteur en tijdstip, vastgepinde
// bovenaan. Bewerken mag enkel de auteur, binnen 15 minuten; daarna enkel een nieuwe notitie.
// Collega's taggen (feedback 28 okt 2026): wie getagd wordt, krijgt de notitie per mail.
// Elke wijziging gaat in de projectlog (dat doet de server).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTITIE_BEWERK_MINUTEN, notitieBewerkbaar } from '@recotex/shared';
import { api, ApiError, type Collega, type NotitieRij } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDateTime, wieLabel } from '../lib/format';
import { useToast } from './Toast';

/** Zonder "toon alle": de vastgepinde plus zoveel recente notities. */
const RECENT = 3;

/** Vraag van buitenaf (knoppen bovenaan de calculatie): ga naar het invoerveld of open de collegalijst. */
export interface NotitieFocus {
  soort: 'notitie' | 'tag';
  n: number;
}

function foutTekst(e: unknown, standaard: string): string {
  const p = e instanceof ApiError ? (e.payload as { error?: string } | null) : null;
  return p?.error || standaard;
}

/** Huidige tijd, elke `ms` ververst: de knop "Bewerken" verdwijnt vanzelf na 15 minuten. */
function useNu(ms: number): Date {
  const [nu, setNu] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNu(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return nu;
}

function opsomming(namen: string[]): string {
  if (namen.length <= 1) return namen.join('');
  return `${namen.slice(0, -1).join(', ')} en ${namen[namen.length - 1]}`;
}

export function Notities({ sleutel, notities, calcId, ingebed = false, focus = null }: {
  sleutel: string;
  notities: NotitieRij[];
  /** Calculatie van waaruit je schrijft: de mail aan getagde collega's linkt ernaar. */
  calcId?: string;
  /** In het dossierpaneel van de calculatie: zonder eigen kaart. */
  ingebed?: boolean;
  focus?: NotitieFocus | null;
}) {
  const { user } = useAuth();
  // /auth/me stuurt de sessie door (veld `userId`); UserPublic noemt het `id`. Beide aanvaarden.
  const mijnId = user?.id || (user as { userId?: string } | null)?.userId || null;
  const qc = useQueryClient();
  const toast = useToast();
  const nu = useNu(20_000);
  const [nieuw, setNieuw] = useState('');
  const [getagd, setGetagd] = useState<Collega[]>([]);
  const [kiezerOpen, setKiezerOpen] = useState(false);
  const [bewerk, setBewerk] = useState<{ id: string; tekst: string } | null>(null);
  const [alles, setAlles] = useState(false);
  const invoerRef = useRef<HTMLTextAreaElement>(null);
  const kaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focus) return;
    kaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    invoerRef.current?.focus({ preventScroll: true });
    if (focus.soort === 'tag') setKiezerOpen(true);
  }, [focus]);

  const verversen = () => {
    qc.invalidateQueries({ queryKey: ['notities'] });
    qc.invalidateQueries({ queryKey: ['projectniveau'] });
    qc.invalidateQueries({ queryKey: ['projectlog'] });
    qc.invalidateQueries({ queryKey: ['project'] });
  };
  const voegToe = useMutation({
    mutationFn: (v: { tekst: string; getagd: Collega[] }) =>
      api.dossier.voegNotitieToe(sleutel, v.tekst, { getagd: v.getagd.map((c) => c.id), calcId }),
    onSuccess: (r, v) => {
      setNieuw('');
      setGetagd([]);
      setKiezerOpen(false);
      verversen();
      if (v.getagd.length) {
        const namen = v.getagd.filter((c) => c.id !== mijnId).map((c) => c.naam);
        if (r.gemaild.length) toast.success(`Notitie geplaatst. ${opsomming(namen)} ${namen.length === 1 ? 'krijgt' : 'krijgen'} een mail.`);
        else if (namen.length) toast.error('Notitie geplaatst, maar de mail kon niet verstuurd worden.');
      }
    },
    onError: (e) => toast.error(foutTekst(e, 'Notitie toevoegen mislukt')),
  });
  const wijzig = useMutation({
    mutationFn: (v: { id: string; body: { tekst?: string; vastgepind?: boolean } }) => api.dossier.wijzigNotitie(v.id, v.body),
    onSuccess: (_r, v) => { if (v.body.tekst !== undefined) setBewerk(null); verversen(); },
    onError: (e) => toast.error(foutTekst(e, 'Notitie wijzigen mislukt')),
  });

  const gesorteerd = [...notities].sort((a, b) => Number(b.vastgepind) - Number(a.vastgepind) || b.aangemaaktOp.localeCompare(a.aangemaaktOp));
  const vast = gesorteerd.filter((n) => n.vastgepind);
  const rest = gesorteerd.filter((n) => !n.vastgepind);
  const zichtbaar = [...vast, ...(alles ? rest : rest.slice(0, RECENT))];
  const verborgen = rest.length - (alles ? rest.length : Math.min(rest.length, RECENT));

  const verstuur = () => {
    const t = nieuw.trim();
    if (t && !voegToe.isPending) voegToe.mutate({ tekst: t, getagd });
  };
  const anderen = getagd.filter((c) => c.id !== mijnId);

  return (
    <div ref={kaderRef} className={`${ingebed ? '' : 'card mb-5 '}normal-case tracking-normal`}>
      {!ingebed && (
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h3 className="text-lg font-bold">Notities{notities.length > 0 && <span className="ml-2 text-sm font-semibold text-secondary">{notities.length}</span>}</h3>
        </div>
      )}

      <form className="mb-4" onSubmit={(e) => { e.preventDefault(); verstuur(); }}>
        <label htmlFor={`nieuwe-notitie-${sleutel}`} className="sr-only">Nieuwe notitie</label>
        <textarea
          ref={invoerRef}
          id={`nieuwe-notitie-${sleutel}`}
          className="textarea text-sm"
          rows={nieuw.includes('\n') || nieuw.length > 80 ? 3 : 2}
          maxLength={5000}
          placeholder={notities.length ? 'Schrijf een notitie…' : 'Nog geen notities. Schrijf de eerste…'}
          value={nieuw}
          onChange={(e) => setNieuw(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); verstuur(); } }}
        />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <CollegaKiezer
            open={kiezerOpen}
            onOpen={setKiezerOpen}
            gekozen={getagd}
            mijnId={mijnId}
            onKies={(c) => setGetagd((g) => (g.some((x) => x.id === c.id) ? g.filter((x) => x.id !== c.id) : [...g, c]))}
          />
          {getagd.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 rounded-full bg-accent-50 text-accent-700 px-2.5 py-0.5 text-xs font-semibold">
              @{c.naam}
              <button type="button" className="hover:text-danger" onClick={() => setGetagd((g) => g.filter((x) => x.id !== c.id))} aria-label={`${c.naam} niet taggen`}>×</button>
            </span>
          ))}
          <button type="submit" className="btn-primary btn-sm ml-auto" disabled={!nieuw.trim() || voegToe.isPending}>
            {voegToe.isPending ? 'Plaatsen…' : anderen.length ? 'Plaatsen en mailen' : 'Notitie plaatsen'}
          </button>
        </div>
        {anderen.length > 0 && (
          <p className="mt-1.5 text-xs text-secondary">{opsomming(anderen.map((c) => c.naam))} {anderen.length === 1 ? 'krijgt' : 'krijgen'} deze notitie per mail, met een link naar {calcId ? 'deze calculatie' : 'dit project'}.</p>
        )}
      </form>

      {zichtbaar.length > 0 && (
        <ul className="list-none m-0 p-0">
          {zichtbaar.map((n) => {
            const eigen = !!mijnId && n.auteurId === mijnId;
            const bewerkbaar = eigen && notitieBewerkbaar(n.aangemaaktOp, nu);
            // Begrensd: `nu` tikt per 20 s en de klok van server en browser kan licht verschillen.
            const minutenOver = Math.min(NOTITIE_BEWERK_MINUTEN, Math.max(1, Math.ceil((NOTITIE_BEWERK_MINUTEN * 60_000 - (nu.getTime() - Date.parse(n.aangemaaktOp))) / 60_000)));
            const isBewerk = bewerk?.id === n.id;
            return (
              <li key={n.id} className={`py-3 first:pt-0 last:pb-0 border-t border-lightgrey first:border-t-0 ${n.vastgepind ? 'pl-3 border-l-[3px] border-l-accent' : ''}`}>
                {isBewerk ? (
                  <div>
                    <label htmlFor={`notitie-${n.id}`} className="sr-only">Notitie bewerken</label>
                    <textarea
                      id={`notitie-${n.id}`}
                      className="textarea text-sm"
                      rows={3}
                      maxLength={5000}
                      autoFocus
                      value={bewerk.tekst}
                      onChange={(e) => setBewerk({ id: n.id, tekst: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setBewerk(null);
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && bewerk.tekst.trim()) wijzig.mutate({ id: n.id, body: { tekst: bewerk.tekst } });
                      }}
                    />
                    <div className="flex gap-2 mt-2">
                      <button type="button" className="btn-primary btn-sm" disabled={!bewerk.tekst.trim() || wijzig.isPending}
                        onClick={() => wijzig.mutate({ id: n.id, body: { tekst: bewerk.tekst } })}>
                        Opslaan
                      </button>
                      <button type="button" className="btn-ghost btn-sm" onClick={() => setBewerk(null)}>Annuleren</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words">{n.tekst}</p>
                )}
                {!!n.getagd?.length && (
                  <p className="mt-1 flex flex-wrap gap-1 text-xs" aria-label="Getagde collega's">
                    {n.getagd.map((g) => <span key={g.id} className="font-semibold text-accent-700">@{g.naam}</span>)}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-secondary">
                  {n.vastgepind && <span className="font-semibold text-accent-700">Vastgepind</span>}
                  <span>{wieLabel(n.auteurNaam, n.persoon)}</span>
                  <span className="tabular-nums">{fmtDateTime(n.aangemaaktOp)}</span>
                  {n.gewijzigdOp && <span className="italic" title={`Bewerkt op ${fmtDateTime(n.gewijzigdOp)}`}>bewerkt</span>}
                  <span className="ml-auto flex gap-3">
                    {bewerkbaar && !isBewerk && (
                      <button type="button" className="font-semibold text-primary hover:underline" onClick={() => setBewerk({ id: n.id, tekst: n.tekst })}
                        title={`Bewerken kan nog ${minutenOver} min`}>
                        Bewerken <span className="font-normal text-secondary">(nog {minutenOver} min)</span>
                      </button>
                    )}
                    <button type="button" className="font-semibold text-primary hover:underline disabled:opacity-50" disabled={wijzig.isPending}
                      onClick={() => wijzig.mutate({ id: n.id, body: { vastgepind: !n.vastgepind } })}>
                      {n.vastgepind ? 'Losmaken' : 'Vastpinnen'}
                    </button>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {(verborgen > 0 || (alles && rest.length > RECENT)) && (
        <button type="button" className="text-xs text-secondary underline mt-3" onClick={() => setAlles((v) => !v)} aria-expanded={alles}>
          {alles ? 'Enkel de recentste tonen' : `Toon alle notities (nog ${verborgen})`}
        </button>
      )}
    </div>
  );
}

/** Knop "@ Collega taggen" met een lijst van collega's (geen gedeelde accounts). */
function CollegaKiezer({ open, onOpen, gekozen, mijnId, onKies }: {
  open: boolean;
  onOpen: (open: boolean) => void;
  gekozen: Collega[];
  mijnId: string | null;
  onKies: (c: Collega) => void;
}) {
  const [zoek, setZoek] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ['collegas'], queryFn: () => api.dossier.collegas(), staleTime: 5 * 60_000 });
  const lijst = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    return (q.data?.collegas ?? []).filter((c) => c.id !== mijnId && (!term || c.naam.toLowerCase().includes(term)));
  }, [q.data, zoek, mijnId]);

  useEffect(() => {
    if (!open) return;
    const weg = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onOpen(false); };
    document.addEventListener('mousedown', weg);
    return () => document.removeEventListener('mousedown', weg);
  }, [open, onOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="btn-ghost btn-sm"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => { onOpen(!open); setZoek(''); }}
        title="Getagde collega's krijgen de notitie per mail"
      >
        @ Collega taggen
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-64 rounded-md border border-border bg-white shadow-lg" onKeyDown={(e) => { if (e.key === 'Escape') onOpen(false); }}>
          <input
            autoFocus
            className="input py-1.5 text-sm border-0 border-b border-border rounded-none rounded-t-md"
            placeholder="Zoek een collega…"
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            aria-label="Collega zoeken"
          />
          <ul role="listbox" aria-multiselectable="true" className="list-none m-0 p-1 max-h-60 overflow-auto">
            {q.isLoading && <li className="px-3 py-2 text-xs text-secondary">Laden…</li>}
            {!q.isLoading && !lijst.length && <li className="px-3 py-2 text-xs text-secondary">Geen collega gevonden</li>}
            {lijst.map((c) => {
              const aan = gekozen.some((g) => g.id === c.id);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={aan}
                    className={`flex w-full items-center justify-between gap-2 rounded px-3 py-1.5 text-left text-sm hover:bg-lightgrey ${aan ? 'font-semibold text-accent-700' : ''}`}
                    onClick={() => onKies(c)}
                  >
                    {c.naam}
                    {aan && <span aria-hidden>✓</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
